import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Trophy, Loader2, History as HistoryIcon, Play, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * WorkoutHistory
 * --------------
 * Lists every completed session for a given workout template, newest first.
 * Each row expands inline to show the per-exercise breakdown (sets, reps,
 * weights) that the user actually lifted that day — so they can see at a
 * glance how this run compared to last week's.
 *
 * Backed by `GET /workouts/{id}/sessions` which returns both exact matches
 * (sessions completed AFTER we started storing workout_id) and legacy
 * lineup-matched sessions, deduped and sorted by completed_at desc.
 */
export default function WorkoutHistory() {
  const { workoutId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/workouts/${workoutId}/sessions`);
        setData(res.data);
      } catch {
        setData({ workout: null, sessions: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, [workoutId]);

  const toggle = (sid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-6 flex items-center gap-2 text-neutral-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
      </div>
    );
  }

  const sessions = data?.sessions || [];
  const wkName = data?.workout?.name || "Workout";

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <button
        onClick={() => nav(-1)}
        data-testid="history-back"
        className="flex items-center text-neutral-400 hover:text-blue-500 text-xs uppercase tracking-widest mb-3"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>
      <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold flex items-center gap-2">
        <HistoryIcon className="w-3 h-3" /> WORKOUT HISTORY
      </div>
      <h1 data-testid="history-title" className="font-display text-4xl sm:text-5xl uppercase leading-none mt-1">
        {wkName}
      </h1>
      <div className="text-sm text-neutral-400 mt-1 mb-6">
        {sessions.length} previous {sessions.length === 1 ? "session" : "sessions"}
      </div>

      {sessions.length === 0 ? (
        <div className="border border-neutral-800 p-10 text-center">
          <Trophy className="w-8 h-8 mx-auto text-neutral-700 mb-2" />
          <div className="text-sm text-neutral-400">
            No sessions yet for this workout. Crush your first one and the
            history starts here.
          </div>
          <Button
            data-testid="history-start-btn"
            onClick={() => nav(`/active/${workoutId}`)}
            className="mt-4 bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10"
          >
            <Play className="w-3 h-3 mr-1.5" /> Start workout
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-neutral-800 border border-neutral-800">
          {sessions.map((s, idx) => {
            const date = new Date(s.completed_at);
            const open = expanded.has(s.session_id);
            const durMin = Math.round((s.duration_seconds || 0) / 60);
            return (
              <div key={s.session_id} data-testid={`history-row-${idx}`} className="bg-neutral-950">
                <button
                  data-testid={`history-toggle-${idx}`}
                  onClick={() => toggle(s.session_id)}
                  className="w-full text-left p-4 hover:bg-neutral-900 flex items-center gap-3"
                >
                  <div className="flex-1">
                    <div className="font-display text-xl uppercase">
                      {date.toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                    </div>
                    <div className="text-xs text-neutral-500 uppercase tracking-wider mt-0.5">
                      {s.total_sets || 0} sets · {Math.round(s.total_weight_kg || 0)}kg total
                      {durMin > 0 && ` · ${durMin}m`}
                      {(s.prs?.length || 0) > 0 && (
                        <span className="ml-2 text-amber-400 font-bold">
                          <Trophy className="inline w-3 h-3 mb-0.5" /> {s.prs.length} PR{s.prs.length > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                  {open ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
                </button>
                {open && (
                  <div data-testid={`history-detail-${idx}`} className="border-t border-neutral-900 bg-neutral-950 p-4 space-y-3">
                    {(s.exercises || []).map((ex, ei) => (
                      <div key={`${ex.exercise_id}-${ei}`} className="border border-neutral-800 p-2.5">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="font-display text-sm uppercase">{ex.name}</div>
                          {ex.tracking_unit === "distance" && (
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">distance</div>
                          )}
                        </div>
                        <div className="grid grid-cols-4 gap-1 text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                          <div>Set</div>
                          <div>Weight</div>
                          <div>{ex.tracking_unit === "distance" ? "Dist" : "Reps"}</div>
                          <div className="text-right">Done</div>
                        </div>
                        {(ex.sets || []).map((st, si) => (
                          <div key={si} className="grid grid-cols-4 gap-1 text-xs py-0.5 border-t border-neutral-900">
                            <div className="text-neutral-400">{si + 1}</div>
                            <div className="font-display">{st.weight ?? 0}<span className="text-[9px] text-neutral-600 ml-0.5">kg</span></div>
                            <div className="font-display">
                              {ex.tracking_unit === "distance"
                                ? <>{st.distance_m ?? 0}<span className="text-[9px] text-neutral-600 ml-0.5">m</span></>
                                : st.reps ?? 0}
                            </div>
                            <div className={`text-right ${st.completed ? "text-emerald-400" : "text-neutral-700"}`}>
                              {st.completed ? "✓" : "—"}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {sessions.length > 0 && (
        <div className="mt-6 flex justify-end">
          <Button
            data-testid="history-start-bottom"
            onClick={() => nav(`/active/${workoutId}`)}
            className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10"
          >
            <Play className="w-3 h-3 mr-1.5" /> Start this workout
          </Button>
        </div>
      )}
    </div>
  );
}
